import * as React from 'react'
import { Loader2, Upload, type LucideIcon } from 'lucide-react'
import { cn } from '../lib/utils'
import { uiText } from '../i18n'

const TEXT = { ru: { loading: 'Загрузка…' }, en: { loading: 'Loading…' } }

/**
 * Dropzone — канон зоны загрузки файла («перетащи/нажми»).
 * Пунктирная рамка + иконка-чип + заголовок + подпись. На ховере рамка → белая.
 * Два варианта фона:
 *  • default — на тёмной странице (bg-muted/30, тёмные токены текста)
 *  • onMedia — поверх произвольного цвета/градиента: тёмная вуаль bg-black/40 +
 *    backdrop-blur + БЕЛЫЙ контент, чтобы читалось на любом фоне (светлом/пёстром).
 */
export interface DropzoneProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: LucideIcon
  /** Идёт загрузка: вместо иконки крутится спиннер, заголовок — «Загрузка…», зона не кликается.
   *  Один вид состояния загрузки на все зоны продукта. */
  loading?: boolean
  /** Подпись под «Загрузка…» (например, проценты). */
  loadingSubtitle?: string
  title: string
  subtitle?: string
  variant?: 'default' | 'onMedia'
}

export const Dropzone = React.forwardRef<HTMLButtonElement, DropzoneProps>(
  (
    {
      icon: BaseIcon = Upload,
      loading = false,
      loadingSubtitle,
      title,
      subtitle,
      variant = 'default',
      className,
      ...props
    },
    ref,
  ) => {
    const onMedia = variant === 'onMedia'
    const Icon = loading ? Loader2 : BaseIcon
    const shownTitle = loading ? uiText(TEXT).loading : title
    const shownSubtitle = loading ? loadingSubtitle : subtitle
    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          'w-full border-2 border-dashed rounded-2xl p-6 text-center transition-all cursor-pointer hover:border-white',
          // Погашенная зона не выглядит кликабельной: ни ховера, ни курсора-руки.
          'disabled:pointer-events-none disabled:opacity-50',
          onMedia
            ? 'border-white/40 bg-black/40 backdrop-blur-sm hover:bg-black/50'
            : 'border-border bg-muted/30 hover:bg-muted/50',
          className,
        )}
        aria-busy={loading || undefined}
        {...props}
        disabled={loading || props.disabled}
      >
        <div
          className={cn(
            'w-12 h-12 rounded-xl mx-auto flex items-center justify-center',
            shownTitle ? 'mb-3' : null,
            onMedia ? 'bg-white/15' : 'bg-muted',
          )}
        >
          <Icon
            className={cn(
              'w-6 h-6',
              onMedia ? 'text-white' : 'text-muted-foreground',
              loading && 'animate-spin',
            )}
          />
        </div>
        {/* Без подписи — один значок (тесная зона: квадрат обложки в шапке). */}
        {shownTitle ? (
          <p className={cn('text-sm font-medium mb-1', onMedia ? 'text-white' : 'text-foreground')}>
            {shownTitle}
          </p>
        ) : null}
        {shownSubtitle && (
          <p className={cn('text-xs', onMedia ? 'text-white/70' : 'text-muted-foreground')}>
            {shownSubtitle}
          </p>
        )}
      </button>
    )
  },
)
Dropzone.displayName = 'Dropzone'
